/**
 * Coordinate extraction utilities for handling mouse and touch events uniformly.
 * Eliminates repeated e.clientX/e.touches[0].clientX patterns throughout the codebase.
 */

/**
 * Get pointer position from any pointer event (mouse, touch, or changedTouches).
 * Returns { clientX, clientY } for compatibility with existing code.
 * @param {Event} e - Mouse event, touch event, or pointer event
 * @returns {{ clientX: number, clientY: number }}
 */
export function getPointerPos(e) {
    if (e.touches && e.touches.length > 0) {
        return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    }
    if (e.changedTouches && e.changedTouches.length > 0) {
        return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
}

/**
 * Get pointer position with semantic property names.
 * Returns { x, y } - alias for getPointerPos but with x/y naming.
 * @param {Event} e - Mouse event, touch event, or pointer event
 * @returns {{ x: number, y: number }}
 */
export function getEventCoords(e) {
    const pos = getPointerPos(e);
    return { x: pos.clientX, y: pos.clientY };
}

/**
 * Get coordinates from a specific touch in a touch event.
 * @param {TouchEvent} e - Touch event
 * @param {number} touchIndex - Index of the touch (default: 0)
 * @returns {{ x: number, y: number } | null} - Returns null if touch doesn't exist
 */
export function getTouchCoords(e, touchIndex = 0) {
    const touches = e.touches || e.changedTouches;
    if (!touches || touches.length <= touchIndex) {
        return null;
    }
    const touch = touches[touchIndex];
    return { x: touch.clientX, y: touch.clientY };
}

/**
 * Get coordinates from a specific touch, returning clientX/clientY format.
 * @param {TouchEvent} e - Touch event
 * @param {number} touchIndex - Index of the touch (default: 0)
 * @returns {{ clientX: number, clientY: number } | null}
 */
export function getTouchPos(e, touchIndex = 0) {
    const touches = e.touches || e.changedTouches;
    if (!touches || touches.length <= touchIndex) {
        return null;
    }
    const touch = touches[touchIndex];
    return { clientX: touch.clientX, clientY: touch.clientY };
}

/**
 * Get center point between two touches (for pinch zoom).
 * @param {TouchEvent} e - Touch event with at least 2 touches
 * @returns {{ x: number, y: number } | null}
 */
export function getTwoTouchCenter(e) {
    if (!e.touches || e.touches.length < 2) {
        return null;
    }
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    return {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2
    };
}

/**
 * Get distance between two touches (for pinch zoom).
 * @param {TouchEvent} e - Touch event with at least 2 touches
 * @returns {number | null}
 */
export function getTwoTouchDistance(e) {
    if (!e.touches || e.touches.length < 2) {
        return null;
    }
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
}
